import * as Sentry from "@sentry/browser";
import EnergyLabelPortal from "./energyLabel/EnergyLabelPortal";

// suppressed because global var from published html for gtm tracking
// eslint-disable-next-line camelcase
declare function c_gtm_trackProductImpressions(products: HTMLAnchorElement[]): void;

export class ProductList {
  private currentPage: number;
  private isFetchInProgress: boolean;
  private isDone: boolean;
  private nextElements: HTMLAnchorElement[];
  private readonly list: HTMLElement;
  private readonly button: HTMLElement;
  private readonly buttonTextOriginal: string;
  private readonly displayLimit: number;
  private buttonClickedWhileInProgress: boolean;

  constructor(list: HTMLElement, button: HTMLElement) {
    this.list = list;
    this.currentPage = 1;
    this.isFetchInProgress = false;
    this.displayLimit = parseInt(this.list.dataset.limit || "36", 10);
    this.nextElements = [];
    this.isDone = false;
    this.buttonClickedWhileInProgress = false;
    this.button = button;
    this.buttonTextOriginal = this.button ? this.button.innerText : "";

    this.button.addEventListener("click", this.onButtonClick);

    const scrollPos = sessionStorage.getItem("scrollPos")?.split("_") ?? [];
    if (scrollPos.length && scrollPos[0] === document.body.id && typeof window.scrollTo === "function") {
      window.scrollTo({left: 0, top: Number(scrollPos[1])});
    }

    window.addEventListener("beforeunload", this.setHistory);

    this.loadNextBatch();
  }

  private onButtonClick = (evt: MouseEvent): void => {
    evt.stopPropagation();
    evt.preventDefault();

    if (this.isFetchInProgress) {
      this.buttonClickedWhileInProgress = true;
      this.buttonDeactivate();

      return;
    }

    this.button.blur();
    this.showNext();
  };

  private buttonDeactivate = () => {
    this.button.innerText = "";
    this.button.classList.remove("button");
    this.button.classList.add("swiper-lazy-preloader");
  };

  private buttonActivate = () => {
    this.button.classList.remove("swiper-lazy-preloader");
    this.button.classList.add("button");
    this.button.innerText = this.buttonTextOriginal;
  };

  private showNext = () => {
    this.buttonClickedWhileInProgress = false;
    this.buttonActivate();

    const next = this.nextElements.splice(0, this.displayLimit);
    next.forEach((el): void => {
      this.list.appendChild(el);
    });
    EnergyLabelPortal.run(next);

    // suppressed because global var from published html for gtm tracking
    // eslint-disable-next-line camelcase
    if (typeof c_gtm_trackProductImpressions === "function") {
      c_gtm_trackProductImpressions(next);
    }

    this.setHistory();

    if (this.isDone && this.nextElements.length <= 0) {
      this.endList();
      this.showSuperCategory();

      return;
    }

    if (this.nextElements.length <= this.displayLimit) {
      this.loadNextBatch();
    }
  };

  private endList = (): void => {
    this.button.style.display = "none";
  };

  private showSuperCategory = (): void => {
    const superButton: HTMLAnchorElement | null = document.querySelector(".button-filter-more");
    if (superButton !== null) {
      superButton.style.display = "";
    }
  };

  private setHistory = (): void => {
    sessionStorage.setItem("scrollPos", [document.body.id, String(window.scrollY)].join("_"));
    const limit = this.list.querySelectorAll(".list-products__item").length;
    const urlSearchParams = new URLSearchParams(window.location.search);
    urlSearchParams.set("limit", limit.toString());
    const newUrl = `${window.location.pathname}?${urlSearchParams.toString()}${window.location.hash}`;
    window.history.replaceState(window.history.state, "undefined", newUrl);

    return;
  };

  private loadNextBatch(): void {
    this.isFetchInProgress = true;
    this.currentPage++;

    if (this.isDone) {
      return;
    }

    fetch(`${window.location.pathname}?page=${this.currentPage}`, {credentials: "include"})
      .then((res) => res.text())
      .then((text) => {
        const dom = new DOMParser().parseFromString(text, "text/html");

        if (dom.documentElement === null) {
          throw new Error("Missing documentElement in target page");
        }

        const list = dom.documentElement.querySelector(".js-product-list-infinite");
        const button = dom.documentElement.querySelector(".js-more-exist");

        if (list === null) {
          throw new Error("Missing node in target page");
        }

        if (button === null) {
          this.setIsDone(true);
        }

        const items = list.querySelectorAll("a");

        items.forEach((el) => {
          const img = el.querySelector("img");
          if (img === null) {
            return;
          }
          img.src = img.dataset.src || img.src;
          img.srcset = img.dataset.srcset || img.srcset;
          img.classList.add("lazy-loaded");

          this.nextElements.push(el);
        });

        this.isFetchInProgress = false;

        if (this.buttonClickedWhileInProgress) {
          this.showNext();
        }
      })
      .catch((e) => {
        Sentry.captureException(e, {extra: {info: "Error while loading mor products"}});
      });
  }

  private setIsDone(value: boolean): void {
    this.isDone = value;
  }
}
